import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { AssessmentService } from 'src/app/services/assessment.service';
import { AuthService } from 'src/app/services/auth.service';
import { CreateAssessmentComponent } from 'src/app/shared/components/create-assessment/create-assessment.component';
import { Project } from 'src/app/shared/models/project.model';

@Component({
    selector: 'app-project-assessment',
    templateUrl: './project-assessment.component.html',
    styleUrl: './project-assessment.component.scss',
    standalone: false
})
export class ProjectAssessmentComponent implements OnInit, OnDestroy {
  @ViewChild('createAssessmentModal') createAssessmentModal!: CreateAssessmentComponent;
  @ViewChild('successModal') successModal!: TemplateRef<any>;

  @Input() currentProject!: Project;
  private searchSubject = new Subject<string>();
  private refreshSubscription!: Subscription;

  createModalRef: any;
  deleteModalRef: any;

  listStatus: any[] = [
		{ name: 'ACTIVE', status_color: 'success'},
		{ name: 'DISABLED', status_color: 'danger'}
	];

  assessments: any[] = [];
  selectedAssessment!: any;
  currentPage: number = 1;
  pageSize: number = 8;
  totalAssessments: number = 0;
  searchText: string = '';
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';

  startIndex: number = 0;
  endIndex: number = 0;

  isAllow = false;
	allowSpecial = false;
	isOwner = false;
	isEditor = false;
	isAnalyst = false;
	allowedRoles = ['OWNER', 'EDITOR', 'ANALYST', 'RESTRICTED'];

  currentUser: any = null;
	current_client: any = null;
	colspan = 0;
  loading = true;
	listloading = true;

  submitted = false;
	spinner = false;
	success = false;
	message = '';
	title = '';

  newCaseId = null;
  newItemId = null;
  newItemType = null;
  actionType: String | null = null;

  constructor(private assessmentService: AssessmentService, private authService: AuthService, private modalService: NgbModal, private router: Router) {}

  ngOnInit(): void {
    this.currentUser = this.authService.currentUserValue;
		this.current_client = this.authService.currentClientValue;

		this.isOwner = this.current_client.roles.includes('OWNER');
		this.isEditor = this.current_client.roles.includes('EDITOR');
		this.isAnalyst = this.current_client.roles.includes('ANALYST');
		this.isAllow = this.current_client.roles.some((role: string) => this.allowedRoles.includes(role));
		this.allowSpecial = this.current_client.roles.includes('RESTRICTED');

		if (this.isAllow) {
			if (!this.authService.currentClientValue.plan) {
				this.isAllow = false;
			}
		}

		this.colspan = this.currentProject ? this.isAllow ? 10 : 9 : this.isAllow ? 11 : 10;

    this.getAssessments();
    
    this.refreshSubscription = this.assessmentService.getRefreshObservable().subscribe(() => {
      this.getAssessments();
    });

    // Subscribe to search input changes with debounce
    this.searchSubject.pipe(
      debounceTime(300), // Adjust debounce time as needed
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1; // Reset to first page when search text changes
      this.getAssessments();
    });

  }

  ngOnDestroy() {
    this.refreshSubscription.unsubscribe();
  }

  openCreateAssessmentModal() {
		this.createModalRef = this.createAssessmentModal.openModal(this.currentProject.id);
	}

  onSearch() {
    this.searchSubject.next(this.searchText);
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.getAssessments();
  }

  getStatusColor(status: string) {
		const s = this.listStatus.find(item => item.name === status);
		return s.status_color;
	}

  afterAssessmentCreation(data: any) {
    this.success = true;
    this.actionType = 'create';
    this.title = data.message;
    this.newCaseId = data.caseId;
    this.newItemId = data.newItemId;
    this.newItemType = data.newItemType;
    this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
  }

  refreshAssessments() {
		this.currentPage = 1;
		this.searchText = '';
		this.getAssessments();
	}

  openDeleteAssessmentModal(assessment: any, content: any) {
    this.actionType = 'delete';
    this.submitted = false;
    this.spinner = false;
    this.selectedAssessment = assessment;
    this.deleteModalRef = this.modalService.open(content, { size: 'l', backdrop: 'static', keyboard : false });
  }

  deleteCase() {
    this.submitted = true;
	  this.spinner = true;

    this.submitted = true;
    this.spinner = true;

    this.assessmentService.deleteAssessment(this.currentProject.id, this.selectedAssessment.id).subscribe({
      next: (res: any) => {
        this.spinner = false;
        this.submitted = false;
        this.success = true;
        this.message = res.message;
        this.deleteModalRef.close();
        this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
      },
      error: (err: any) => {
        this.spinner = false;
        this.submitted = false;
        this.success = false;
        this.message = err.error?.message || err.message;
        this.deleteModalRef.close();
        this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
      }
    });
  }

  closeModal() {
		if (this.success) {
      if (this.actionType === 'delete') {
        this.refreshAssessments();
      } else {
        if (this.newItemType === 'examinee') {
          this.router.navigate(['/projects/' + this.currentProject.id + '/assessment/' + this.newCaseId]);
        } else {
          this.router.navigate(['/projects/' + this.currentProject.id + '/assessment/' + this.newCaseId + '/conversation/' + this.newItemId]);
        }
      }
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}

  getAssessments() {
		this.loading = true;
    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };

    this.assessmentService.getProjectAssessments(this.currentProject.id, params).subscribe({
      next: (response: any) => {
        this.assessments = response.cases;
        this.totalAssessments = response.totalCases;
        this.calculateIndexes();
        this.loading = false;
      },
      error: (err: any) => {
        this.authService.toastErrorMessage(err.message ? err.message : err, 'Project Assessments');
      }
    });
  }

  calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalAssessments);
  }

}
